import type { DecimalString } from "@shop-ledger/shared-types";
import { createPurchaseOrder, receivePurchaseOrder } from "../api/purchaseOrdersApi";

export interface TopUpStockParams {
  branchId: string;
  itemId: string;
  /** Callers must pass an already-validated `DecimalString` (`asDecimalString(...)`) — this
   * helper doesn't parse/validate raw user input itself. */
  quantity: DecimalString;
  unitCost: DecimalString;
  supplierId?: string;
  /** Multi-batch tracking — both optional, so a top-up that doesn't care about batching
   * behaves exactly as before this existed. */
  batchNumber?: string;
  expiryDate?: string;
}

/**
 * "Top Up Stock" (business-owner walkthrough follow-up) — no dedicated backend endpoint
 * exists for a direct stock increase, so this composes the two existing, already-tested
 * purchase-order calls instead of adding a second way to mutate `current_stock`:
 * `POST /purchase-orders` (single line, `supplier_id` optional) immediately followed by
 * `POST /purchase-orders/{id}/receive` for that same line/quantity.
 *
 * Extracted out of `TopUpStockModal` so `ScanToAddStockModal`'s scan-driven restock loop
 * can reuse the exact same call sequence rather than duplicating it. Throws `ApiError` on
 * failure — callers already handle `status === 409` specially (a tenant with an approval
 * rule routes new POs to PENDING_APPROVAL, so the receive call 409s: the PO exists but
 * stock genuinely hasn't moved yet).
 */
export async function topUpStock(accessToken: string, params: TopUpStockParams): Promise<void> {
  const po = await createPurchaseOrder(accessToken, {
    branch_id: params.branchId,
    supplier_id: params.supplierId || undefined,
    lines: [{ inventory_item_id: params.itemId, quantity_ordered: params.quantity, unit_cost: params.unitCost }],
  });
  const line = po.lines[0];
  if (!line) throw new Error("Purchase order was created with no lines — this should never happen.");
  await receivePurchaseOrder(accessToken, po.id, {
    lines: [
      {
        line_id: line.id,
        quantity_received: params.quantity,
        batch_number: params.batchNumber || undefined,
        expiry_date: params.expiryDate || undefined,
      },
    ],
  });
}
